import { useState, useEffect } from 'react';
import AppLayout from '@/components/feature/AppLayout';

interface Alert {
  id: string;
  type: 'critical' | 'warning' | 'info' | 'reminder';
  title: string;
  description: string;
  caseNumber: string;
  dueDate: string;
  read: boolean;
  createdAt: string;
}

const initialAlerts: Alert[] = [
  { id: 'al1', type: 'critical', title: 'Critical Case Requires Immediate Action', description: 'DCPCMS-2026-00006 (Liza Fernandez) has been escalated to critical. Emergency home visit required within 24 hours.', caseNumber: 'DCPCMS-2026-00006', dueDate: '2026-04-10', read: false, createdAt: '2026-04-09 08:30' },
  { id: 'al2', type: 'warning', title: 'Supervisor Review Pending', description: 'DCPCMS-2026-00002 (Ana Reyes) has been pending supervisor approval for 2 days. Action required.', caseNumber: 'DCPCMS-2026-00002', dueDate: '2026-04-11', read: false, createdAt: '2026-04-09 09:00' },
  { id: 'al3', type: 'reminder', title: 'Monthly Report Due in 3 Days', description: 'The April 2026 monthly child protection report is due on April 12, 2026. Please ensure all cases are updated.', caseNumber: '', dueDate: '2026-04-12', read: false, createdAt: '2026-04-09 07:00' },
  { id: 'al4', type: 'info', title: 'Home Visit Scheduled', description: 'DCPCMS-2026-00001 (Juan Dela Cruz) — Scheduled home visit on April 11, 2026 at 10:00 AM.', caseNumber: 'DCPCMS-2026-00001', dueDate: '2026-04-11', read: true, createdAt: '2026-04-08 14:00' },
  { id: 'al5', type: 'warning', title: 'Document Upload Overdue', description: 'DCPCMS-2026-00007 (Miguel Torres) — Intake documents have not been uploaded. Please complete within 48 hours.', caseNumber: 'DCPCMS-2026-00007', dueDate: '2026-04-11', read: true, createdAt: '2026-04-08 10:30' },
  { id: 'al6', type: 'info', title: 'Risk Assessment Due', description: 'DCPCMS-2026-00003 (Pedro Villanueva) — 30-day risk reassessment is due on April 15, 2026.', caseNumber: 'DCPCMS-2026-00003', dueDate: '2026-04-15', read: true, createdAt: '2026-04-07 09:00' },
  { id: 'al7', type: 'reminder', title: 'Inter-Agency Meeting', description: 'Scheduled meeting with PNP WCPD and DOJ PAO on April 13, 2026 at 2:00 PM regarding DCPCMS-2026-00004.', caseNumber: 'DCPCMS-2026-00004', dueDate: '2026-04-13', read: false, createdAt: '2026-04-06 15:00' },
  { id: 'al8', type: 'critical', title: 'Follow-up Overdue', description: 'DCPCMS-2026-00001 (Juan Dela Cruz) — Scheduled follow-up was due 3 days ago. Please update immediately.', caseNumber: 'DCPCMS-2026-00001', dueDate: '2026-04-06', read: false, createdAt: '2026-04-06 08:00' },
];

const typeConfig = {
  critical: { icon: 'ri-alarm-warning-line', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-500' },
  warning: { icon: 'ri-error-warning-line', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-500' },
  info: { icon: 'ri-information-line', color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-200', badge: 'bg-teal-500' },
  reminder: { icon: 'ri-calendar-event-line', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-500' },
};

export default function AlertsPage() {
  const [alerts, setAlerts] = useState<Alert[]>(initialAlerts);
  const [filter, setFilter] = useState<'all' | 'unread' | Alert['type']>('all');
  const [showNewModal, setShowNewModal] = useState(false);
  const [newAlert, setNewAlert] = useState({ title: '', description: '', type: 'reminder' as Alert['type'], caseNumber: '', dueDate: '' });
  const [lastSync, setLastSync] = useState(new Date().toLocaleTimeString());

  // Simulate real-time updates every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setLastSync(new Date().toLocaleTimeString());
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const filtered = alerts.filter((a) => {
    if (filter === 'unread') return !a.read;
    if (filter === 'all') return true;
    return a.type === filter;
  });

  const unreadCount = alerts.filter((a) => !a.read).length;

  const markRead = (id: string) => setAlerts((prev) => prev.map((a) => a.id === id ? { ...a, read: true } : a));
  const markAllRead = () => setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
  const dismiss = (id: string) => setAlerts((prev) => prev.filter((a) => a.id !== id));

  const handleAddAlert = () => {
    if (!newAlert.title) return;
    setAlerts((prev) => [{
      id: `al${Date.now()}`,
      ...newAlert,
      read: false,
      createdAt: new Date().toLocaleString(),
    }, ...prev]);
    setShowNewModal(false);
    setNewAlert({ title: '', description: '', type: 'reminder', caseNumber: '', dueDate: '' });
  };

  return (
    <AppLayout title="Alerts & Reminders" breadcrumb={['Home', 'Alerts & Reminders']}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-teal-600 bg-teal-50 border border-teal-200 px-3 py-1.5 rounded-lg">
            <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
            <span>Live — Last updated: {lastSync}</span>
          </div>
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs px-2.5 py-1 rounded-full font-bold">{unreadCount} unread</span>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={markAllRead} className="text-sm border border-gray-200 text-gray-600 px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">Mark All Read</button>
          <button onClick={() => setShowNewModal(true)} className="text-sm bg-teal-500 text-white px-4 py-2 rounded-xl hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            <i className="ri-add-line" />Add Reminder
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {(['critical', 'warning', 'reminder', 'info'] as Alert['type'][]).map((type) => {
          const count = alerts.filter((a) => a.type === type).length;
          const cfg = typeConfig[type];
          return (
            <button key={type} onClick={() => setFilter(type)} className={`${cfg.bg} border ${cfg.border} rounded-xl p-4 text-left cursor-pointer hover:opacity-90 transition-opacity ${filter === type ? 'ring-2 ring-offset-1 ring-teal-400' : ''}`}>
              <div className={`w-8 h-8 flex items-center justify-center mb-2`}>
                <i className={`${cfg.icon} ${cfg.color} text-xl`} />
              </div>
              <p className={`text-2xl font-bold ${cfg.color}`}>{count}</p>
              <p className="text-xs text-gray-600 capitalize mt-0.5">{type}</p>
            </button>
          );
        })}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {(['all', 'unread', 'critical', 'warning', 'reminder', 'info'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-sm px-4 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap capitalize
              ${filter === f ? 'bg-teal-500 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          >
            {f === 'unread' ? `Unread (${unreadCount})` : f}
          </button>
        ))}
      </div>

      {/* Alerts List */}
      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <i className="ri-notification-off-line text-4xl text-gray-300 mb-3 block" />
            <p className="text-gray-500">No alerts in this category</p>
          </div>
        )}
        {filtered.map((alert) => {
          const cfg = typeConfig[alert.type];
          return (
            <div key={alert.id} className={`bg-white rounded-xl border ${!alert.read ? cfg.border : 'border-gray-100'} p-5 transition-all`}>
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 ${cfg.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <i className={`${cfg.icon} ${cfg.color} text-lg`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`text-sm font-semibold ${!alert.read ? 'text-gray-900' : 'text-gray-600'}`}>{alert.title}</p>
                      {!alert.read && <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!alert.read && (
                        <button onClick={() => markRead(alert.id)} className="text-xs text-teal-500 hover:text-teal-700 cursor-pointer whitespace-nowrap">Mark Read</button>
                      )}
                      <button onClick={() => dismiss(alert.id)} className="text-gray-300 hover:text-red-400 cursor-pointer transition-colors">
                        <i className="ri-close-line" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{alert.description}</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    {alert.caseNumber && (
                      <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{alert.caseNumber}</span>
                    )}
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <i className="ri-calendar-line" />Due: {alert.dueDate}
                    </span>
                    <span className="text-xs text-gray-400">{alert.createdAt}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize font-medium ${cfg.bg} ${cfg.color}`}>{alert.type}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Reminder Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">Add New Reminder</h3>
              <button onClick={() => setShowNewModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Title <span className="text-red-500">*</span></label>
                <input value={newAlert.title} onChange={(e) => setNewAlert({ ...newAlert, title: e.target.value })} placeholder="Alert title" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Type</label>
                <select value={newAlert.type} onChange={(e) => setNewAlert({ ...newAlert, type: e.target.value as Alert['type'] })} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 cursor-pointer">
                  <option value="reminder">Reminder</option>
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Description</label>
                <textarea value={newAlert.description} onChange={(e) => setNewAlert({ ...newAlert, description: e.target.value })} rows={3} maxLength={500} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Case Number</label>
                  <input value={newAlert.caseNumber} onChange={(e) => setNewAlert({ ...newAlert, caseNumber: e.target.value })} placeholder="Optional" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Due Date</label>
                  <input type="date" value={newAlert.dueDate} onChange={(e) => setNewAlert({ ...newAlert, dueDate: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500" />
                </div>
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-5">
              <button onClick={() => setShowNewModal(false)} className="text-sm border border-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50">Cancel</button>
              <button onClick={handleAddAlert} className="text-sm bg-teal-500 text-white px-5 py-2 rounded-xl hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap">Add Reminder</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
